/* Stigg (pricing/entitlement) config for Tokenwise (OPSS-55).
 *
 * Mirrors studio-web's SaaS-vs-noop split, but keyed on the presence of a
 * public client key rather than a VITE_DEPLOYMENT_TYPE flag (which this app
 * doesn't have). No key ⇒ the noop service is used and nothing Stigg-related
 * renders or calls out — the safe default for local/dev/on-prem. */

import { IDENTITY_BASE_URL } from "../../auth/config";

function readEnv(key: string): string | undefined {
  if (typeof window === "undefined") return undefined;

  const value = window.__env__?.[key];
  return value && value.length > 0 ? value : undefined;
}

/** Stigg public client-side key. Safe to expose; hardening (setCustomerId +
 *  signed customer token) is what actually authorizes per-customer reads. */
export const STIGG_CLIENT_KEY = readEnv("VITE_STIGG_CLIENT_KEY");

/** Stigg is active only when a client key is configured. */
export const STIGG_ENABLED = Boolean(STIGG_CLIENT_KEY);

/** Identity endpoint that mints the per-customer hardening token. Identity mounts
 *  all routes under /identity (PREFIX) + the stigg router's own /stigg prefix, so the
 *  full path is /identity/stigg/customer-token. */
export const STIGG_CUSTOMER_TOKEN_URL = IDENTITY_BASE_URL
  ? `${IDENTITY_BASE_URL}/identity/stigg/customer-token`
  : undefined;

/** The Intelligent Gateway Stigg product — scopes the paywall to its plans (not Studio's). */
export const TOKENWISE_PRODUCT_ID = "product-intelligent-gateway";
/** Boolean feature the Intelligent Gateway Pro plan grants; drives access + trial state. */
export const TOKENWISE_FEATURE_ID = "feature-intelligent-gateway-access";
/** The Intelligent Gateway Pro plan (monthly/annual, 30-day trial). */
export const TOKENWISE_PLAN_ID = "plan-intelligent-gateway-pro";

/** Brand theme for Stigg's widgets (paywall + checkout) so they match the app instead of
 *  Stigg's defaults: AI21/Tokenwise fuchsia palette, the app's Aeonik Pro font, and the
 *  design-system card radius (rounded-lg = 8px). Palette values from the brand ramp:
 *  brand-600 (#d01554) primary button, brand-500 (#e72164) fuchsia, brand-700 dark. */
export const STIGG_THEME = {
  palette: {
    // Fuchsia brand ramp: brand-600 primary button, brand-500 fuchsia, brand-700 dark.
    primary: "#d01554",
    primaryDark: "#af0e48",
    primaryLight: "#e72164",
    // Light-pink button background (brand-100) so the fuchsia `primary` label stays
    // legible on it (same fuchsia as `primary` made the CTA label invisible until hover).
    backgroundButton: "#fce4ed",
    // Surfaces from the design system: surface (grey-0) + surface-muted (grey-200).
    backgroundPaper: "#ffffff",
    backgroundSection: "#ecf0f1",
    // Text ramp from the design system: fg-1..fg-4.
    text: {
      primary: "#0d1013",
      secondary: "#495055",
      tertiary: "#8e9ba0",
      disabled: "#a9b3ba",
    },
  },
  typography: {
    // Match the app's self-hosted webfont (see design-system/styles/ai21-brand.css).
    fontFamily: '"Aeonik Pro", system-ui, -apple-system, sans-serif',
  },
  border: {
    // Match the design-system card radius (--ai21-radius-lg / rounded-lg = 12px).
    radius: "12px",
  },
  shadows: {
    // Design-system elevation tokens (--ai21-shadow-default / --ai21-shadow-lg).
    light: "0 1px 3px rgba(13, 16, 19, 0.08), 0 1px 2px rgba(13, 16, 19, 0.04)",
    popover:
      "0 10px 20px rgba(13, 16, 19, 0.1), 0 3px 6px rgba(13, 16, 19, 0.05)",
  },
};
